import { ArrowDown, ArrowUp, Minus, Equal, SlidersHorizontal } from 'lucide-react';
import type { SegmentFilter, TreasuryReport } from '@odd/domain';
import { InfoTip } from './ui';
import { treasuryEuro, treasuryThresholdRanges } from '../lib/treasuryPresentation';
import './TreasurySummary.css';

interface TreasurySummaryData {
  out: number;
  f1r: number;
  f1a: number;
  f2r: number;
  f2a: number;
  negative: number;
  liquidez: number;
  deuda: number;
  impagados: number;
  gastos: number;
}

interface TreasurySummaryProps {
  summary: TreasurySummaryData;
  total: number;
  missing: number;
  segment: SegmentFilter;
  thresholds?: TreasuryReport['thresholds'];
  isLoading: boolean;
}

function StatusCounts({ red, amber, isLoading }: { red: number; amber: number; isLoading: boolean }) {
  return (
    <dl className="treasury-summary-status-counts">
      <div className="treasury-summary-tone--bad">
        <dt>
          <i aria-hidden="true" />
          Rojo
        </dt>
        <dd>{isLoading ? '—' : red}</dd>
      </div>
      <div className="treasury-summary-tone--warn">
        <dt>
          <i aria-hidden="true" />
          Ámbar
        </dt>
        <dd>{isLoading ? '—' : amber}</dd>
      </div>
    </dl>
  );
}

function ThresholdRanges({
  title,
  direction,
  values,
}: {
  title: string;
  direction: 'down' | 'up';
  values: string[];
}) {
  const DirectionIcon = direction === 'down' ? ArrowDown : ArrowUp;
  return (
    <section
      className="treasury-summary-threshold-metric"
      aria-label={`Umbrales de ${title.toLocaleLowerCase('es')}`}
    >
      <div className="treasury-summary-threshold-label">
        <h4>{title}</h4>
        <span>
          <DirectionIcon size={12} aria-hidden="true" />
          {direction === 'down' ? 'Menos meses, más riesgo' : 'Más porcentaje, más riesgo'}
        </span>
      </div>
      <dl className="treasury-summary-ranges">
        {(['bad', 'warn', 'ok'] as const).map((tone, i) => (
          <div className={`treasury-summary-tone--${tone}`} key={tone}>
            <dt>
              <i aria-hidden="true" />
              {['Rojo', 'Ámbar', 'Verde'][i]}
            </dt>
            <dd>{values[i]}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function TreasurySummary({
  summary: s,
  total,
  missing,
  segment,
  thresholds,
  isLoading,
}: TreasurySummaryProps) {
  const money = (value: number) => (isLoading ? '—' : treasuryEuro(value));
  const available = s.liquidez - s.deuda;
  const ranges = thresholds ? treasuryThresholdRanges(thresholds) : null;
  return (
    <section className="treasury-summary" aria-label="Resumen de finanzas operativas" aria-busy={isLoading}>
      <div className="treasury-summary-caption">
        <h3>Resumen financiero</h3>
        <span>{segment === 'AB' ? 'Comunidades A + B' : 'Todos los segmentos'}</span>
      </div>
      <div className="treasury-summary-primary">
        <section className="treasury-summary-card treasury-summary-out" aria-label="Fuera de umbral">
          <h4>
            Fuera de umbral
            <InfoTip label="Qué cuenta el total fuera de umbral">
              Comunidades con cobertura o morosidad en rojo o ámbar. Si una comunidad aparece en ambos
              indicadores, aquí solo se cuenta una vez.
            </InfoTip>
          </h4>
          <p className="treasury-summary-total">
            <strong className={s.out ? 'treasury-summary-value--bad' : undefined}>
              {isLoading ? '—' : s.out}
            </strong>
            <span>de {isLoading ? '—' : total}</span>
          </p>
          <p className="treasury-summary-sub">
            comunidades con saldo{missing > 0 && <span> · {missing} sin datos</span>}
          </p>
        </section>
        <section className="treasury-summary-card" aria-label="Cobertura de caja">
          <h4>Cobertura de caja</h4>
          <StatusCounts red={s.f1r} amber={s.f1a} isLoading={isLoading} />
          <p className="treasury-summary-sub">{isLoading ? '—' : s.negative} con disponible negativo</p>
        </section>
        <section className="treasury-summary-card" aria-label="Índice de morosidad">
          <h4>Índice de morosidad</h4>
          <StatusCounts red={s.f2r} amber={s.f2a} isLoading={isLoading} />
          <p className="treasury-summary-sub">comunidades por nivel de alerta</p>
        </section>
        <section className="treasury-summary-card" aria-label="Impagados">
          <h4>Impagados</h4>
          <p className="treasury-summary-amount">{money(s.impagados)}</p>
          <p className="treasury-summary-sub">sobre {money(s.gastos)} de gasto anual</p>
        </section>
      </div>
      <dl className="treasury-summary-cash" aria-label="Posición de caja">
        <div>
          <dt>Bancos + caja</dt>
          <dd>{money(s.liquidez)}</dd>
        </div>
        <div>
          <Minus className="treasury-summary-operation" size={15} aria-hidden="true" />
          <dt>Deuda a proveedores</dt>
          <dd>{money(s.deuda)}</dd>
        </div>
        <div className="treasury-summary-available">
          <Equal className="treasury-summary-operation" size={15} aria-hidden="true" />
          <dt>Disponible</dt>
          <dd className={available < 0 ? 'treasury-summary-value--bad' : undefined}>{money(available)}</dd>
        </div>
      </dl>
      <section className="treasury-summary-thresholds" aria-label="Umbrales de finanzas operativas">
        <div className="treasury-summary-threshold-heading">
          <SlidersHorizontal size={16} strokeWidth={1.5} aria-hidden="true" />
          <h3>Umbrales</h3>
          <span>Cuándo cambia el color de cada comunidad</span>
        </div>
        {thresholds ? (
          <div className="treasury-summary-threshold-grid">
            <ThresholdRanges title="Cobertura de caja" direction="down" values={ranges!.coverage} />
            <ThresholdRanges title="Morosidad" direction="up" values={ranges!.debt} />
          </div>
        ) : (
          <p className="treasury-summary-threshold-empty" role="status">
            {isLoading
              ? 'Cargando los límites del informe…'
              : 'No hay límites disponibles para este informe.'}
          </p>
        )}
        <p className="treasury-summary-threshold-note">
          Con disponible negativo, la cobertura siempre es roja.
        </p>
      </section>
    </section>
  );
}
